// useDictation — state machine + Transcribe-streaming wiring for the Coach
// composer mic. Scoped to the composer; the DictationStrip, mic button, and
// composer partial-text display all read from one instance of this hook.
//
// Designer handoff: design/UI : UX v1.0/for-designer-consumer-dictation-v1/
// (README.md walks the 8 states; source-editable.html is the visual spec).
//
// Guarantees we uphold explicitly:
//   - Pause actually releases the MediaStreamTrack (OS mic indicator goes
//     away). Resume opens a fresh track + Transcribe session.
//   - 5-minute hard cap. The timer fires end-session even if the user is
//     mid-utterance; text already transcribed stays in the composer.
//   - ⌘⇧M / Ctrl+Shift+M starts dictation from anywhere on the page. Esc
//     ends an active session. Both only fire from the composer context.
//   - One session at a time. Starting dictation while another is live ends
//     the first (same hook instance; the component owns singletonhood).
//   - All AWS calls are eu-west-1. The Identity Pool role grant also pins
//     this and the three allowed languages (fr-FR, en-US, en-GB).

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  TranscribeStreamingClient,
  StartStreamTranscriptionCommand,
} from '@aws-sdk/client-transcribe-streaming';
import { fromCognitoIdentityPool } from '@aws-sdk/credential-provider-cognito-identity';
import { env } from '../../config/env.js';
import { loadTokens } from '../../auth/tokenStorage.js';
import { openPcmPipeline, type PcmPipeline } from './pcmEncoder.js';

export type DictationState =
  | 'idle'
  | 'permission' // showing the pre-permission explainer (state 2)
  | 'denied' // mic blocked at the OS/browser (state 3)
  | 'live' // actively transcribing (state 4)
  | 'paused' // user hit pause (state 5)
  | 'ending-soon' // 4:30+ elapsed, 30s left of the 5-min cap (state 6)
  | 'ended' // session finished cleanly (state 7, shown for 4s then → idle)
  | 'error'; // Transcribe or mic hardware failed (state 8)

export type DictationLang = 'EN' | 'FR';
export type DictationErrorKind = 'network' | 'mic' | 'unknown';

export interface DictationApi {
  state: DictationState;
  /** Finalised text produced in this session. Appended to the composer at
   *  Stop time (or incrementally — the component decides). */
  finalText: string;
  /** The trailing partial (unstable, re-written on each new partial from
   *  Transcribe). Rendered dotted-underlined in grey. */
  partialText: string;
  /** Elapsed seconds since Start. Resets to 0 on Pause→Resume (per the
   *  designer walkthrough — the 5-min cap is per Resume cycle, not
   *  cumulative; the strip timer visibly resets). */
  elapsedSec: number;
  /** Detected language from Transcribe, or null before the first final. */
  detectedLang: DictationLang | null;
  /** Words added so far (used by the "Dictation added {n} words" toast). */
  wordCount: number;
  /** Which error kind, when state === 'error'. */
  errorKind: DictationErrorKind | null;

  /** Start the pre-permission explainer (state 1 → 2). */
  requestStart: () => void;
  /** User confirmed in the explainer (state 2 → 4). */
  grantAndStart: () => Promise<void>;
  /** User dismissed the explainer (state 2 → 1). */
  cancelStart: () => void;
  /** Pause the current session (state 4 → 5). */
  pause: () => void;
  /** Resume from pause (state 5 → 4). */
  resume: () => Promise<void>;
  /** End the session cleanly (state 4|5 → 7 → 1). The dictated text stays
   *  in finalText for the component to drain. */
  stop: () => void;
  /** Discard — same as stop but clears finalText too (state 4|5 → 1). */
  discard: () => void;
}

const MAX_SESSION_SEC = 300; // 5 minutes
const ENDING_SOON_AT_SEC = 270; // 4:30 (strip changes copy + ring pulses)
const ENDED_DWELL_MS = 4000; // state 7 shows for 4s then → idle
// How long a partial must stay unchanged before we promote it to a final
// client-side. Transcribe's natural silence detection takes 5–10s which
// feels broken for live dictation; promoting at 1.2s keeps text flowing
// into the composer at the cadence of speech.
const STABLE_PARTIAL_MS = 1200;

export function useDictation(): DictationApi {
  const [state, setState] = useState<DictationState>('idle');
  const [finalText, setFinalText] = useState('');
  const [partialText, setPartialText] = useState('');
  const [elapsedSec, setElapsedSec] = useState(0);
  const [detectedLang, setDetectedLang] = useState<DictationLang | null>(null);
  const [wordCount, setWordCount] = useState(0);
  const [errorKind, setErrorKind] = useState<DictationErrorKind | null>(null);

  // Mutable per-session handles — refs so the hook can tear them down from
  // any state transition without triggering a re-render.
  const pipelineRef = useRef<PcmPipeline | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const tickRef = useRef<number | null>(null);
  const endedDwellRef = useRef<number | null>(null);
  // Partial auto-commit — Transcribe's natural finalisation lags silence by
  // 5–10s. We promote a stable partial to a final after STABLE_PARTIAL_MS
  // of no change so the composer value grows at the same cadence as speech.
  const stablePartialRef = useRef<string>('');
  const partialTimerRef = useRef<number | null>(null);
  const autoCommittedRef = useRef<string>('');

  const clearTick = useCallback(() => {
    if (tickRef.current !== null) {
      window.clearInterval(tickRef.current);
      tickRef.current = null;
    }
  }, []);

  const teardownPipeline = useCallback(() => {
    if (partialTimerRef.current !== null) {
      window.clearTimeout(partialTimerRef.current);
      partialTimerRef.current = null;
    }
    stablePartialRef.current = '';
    autoCommittedRef.current = '';
    try {
      pipelineRef.current?.stop();
    } catch { /* already stopped */ }
    pipelineRef.current = null;
    try {
      abortRef.current?.abort();
    } catch { /* already aborted */ }
    abortRef.current = null;
  }, []);

  // Append a chunk of transcribed text to the composer value. Called from
  // both the real-final path and the auto-commit path.
  const appendFinal = useCallback((text: string) => {
    if (!text) return;
    setFinalText((prev) => {
      const sep = prev && !prev.endsWith(' ') ? ' ' : '';
      const next = prev + sep + text;
      setWordCount(countWords(next));
      return next;
    });
  }, []);

  // State 7 → 1 auto-transition (ENDED_DWELL_MS after a clean stop).
  useEffect(() => {
    if (state !== 'ended') return;
    endedDwellRef.current = window.setTimeout(() => {
      setState('idle');
      setPartialText('');
      // finalText intentionally NOT cleared here — the composer drains it
      // on its own schedule via a useEffect watching state === 'ended'.
    }, ENDED_DWELL_MS);
    return () => {
      if (endedDwellRef.current !== null) window.clearTimeout(endedDwellRef.current);
      endedDwellRef.current = null;
    };
  }, [state]);

  // Elapsed-second ticker — active in live + ending-soon only.
  useEffect(() => {
    if (state !== 'live' && state !== 'ending-soon') return;
    tickRef.current = window.setInterval(() => {
      setElapsedSec((prev) => {
        const next = prev + 1;
        if (next >= MAX_SESSION_SEC) {
          // 5-minute cap — end the session. Done inside the ticker to
          // avoid a race between the timeout and new audio arriving.
          window.setTimeout(() => stopInternal('timeout'), 0);
          return next;
        }
        if (next >= ENDING_SOON_AT_SEC && state === 'live') {
          setState('ending-soon');
        }
        return next;
      });
    }, 1000);
    return clearTick;
  }, [state]);

  const start = useCallback(async () => {
    if (!env.dictationIdentityPoolId) {
      setErrorKind('unknown');
      setState('error');
      return;
    }
    const tokens = loadTokens();
    if (!tokens) {
      setErrorKind('unknown');
      setState('error');
      return;
    }

    let pipeline: PcmPipeline;
    try {
      pipeline = await openPcmPipeline();
    } catch (err) {
      const name = err instanceof Error ? err.name : '';
      if (name === 'NotAllowedError' || name === 'SecurityError') {
        setState('denied');
      } else {
        setErrorKind('mic');
        setState('error');
      }
      return;
    }
    pipelineRef.current = pipeline;

    const client = new TranscribeStreamingClient({
      region: env.cognitoRegion,
      credentials: fromCognitoIdentityPool({
        clientConfig: { region: env.cognitoRegion },
        identityPoolId: env.dictationIdentityPoolId,
        logins: {
          [`cognito-idp.${env.cognitoRegion}.amazonaws.com/${env.cognitoUserPoolId}`]:
            tokens.idToken,
        },
      }),
    });

    const abort = new AbortController();
    abortRef.current = abort;

    const cmd = new StartStreamTranscriptionCommand({
      MediaEncoding: 'pcm',
      MediaSampleRateHertz: 16000,
      // V1 locks to en-US for sub-second partials. IdentifyLanguage adds
      // ~20-30s of latency before the first result and tends to batch
      // finals, making it unusable for live dictation. V1.1 adds a user
      // profile preference (en-US | fr-FR | auto) that reopens the
      // IdentifyLanguage path when the user explicitly asks for it.
      LanguageCode: 'en-US',
      // Transcribe's default silence-detect window on finals is 5–10s,
      // which feels broken for a live-dictation UX. Stabilization 'high'
      // splits each partial into a stable prefix + volatile tail; the
      // stable prefix converges to the final text within ~1s and the
      // service promotes it to a final much faster.
      EnablePartialResultsStabilization: true,
      PartialResultsStability: 'high',
      AudioStream: pipeline.stream,
    });

    setState('live');
    setElapsedSec(0);
    setPartialText('');
    setErrorKind(null);

    (async () => {
      try {
        const response = await client.send(cmd, { abortSignal: abort.signal });
        if (!response.TranscriptResultStream) {
          throw new Error('Transcribe returned no result stream');
        }
        for await (const event of response.TranscriptResultStream) {
          if (abort.signal.aborted) break;
          const results = event.TranscriptEvent?.Transcript?.Results ?? [];
          for (const r of results) {
            const alt = r.Alternatives?.[0];
            const text = alt?.Transcript ?? '';
            if (r.IsPartial) {
              setPartialText(text);
              stablePartialRef.current = text;
              // (Re)start the stability timer. If the text doesn't change
              // for STABLE_PARTIAL_MS, promote it to a final so it lands
              // in the composer at the cadence of speech instead of
              // waiting on Transcribe's slow silence detection.
              if (partialTimerRef.current !== null) {
                window.clearTimeout(partialTimerRef.current);
              }
              partialTimerRef.current = window.setTimeout(() => {
                const stable = stablePartialRef.current;
                partialTimerRef.current = null;
                if (!stable) return;
                setPartialText('');
                stablePartialRef.current = '';
                autoCommittedRef.current = stable;
                appendFinal(stable);
              }, STABLE_PARTIAL_MS);
            } else {
              // Real final from Transcribe. Cancel any pending auto-commit
              // and dedupe against what we may have already promoted.
              if (partialTimerRef.current !== null) {
                window.clearTimeout(partialTimerRef.current);
                partialTimerRef.current = null;
              }
              setPartialText('');
              stablePartialRef.current = '';
              const committed = autoCommittedRef.current;
              autoCommittedRef.current = '';
              // If we already committed this exact text, skip. If
              // Transcribe's final extends the auto-committed text, append
              // only the new tail. Otherwise append as-is.
              if (committed && text === committed) {
                // dedup — no-op
              } else if (committed && text.startsWith(committed)) {
                appendFinal(text.slice(committed.length).trimStart());
              } else {
                appendFinal(text);
              }
              // Transcribe returns the detected language code on finals
              // when IdentifyLanguage is on. Map to our EN/FR flag.
              const lang = r.LanguageCode;
              if (lang) {
                setDetectedLang(lang.startsWith('fr') ? 'FR' : 'EN');
              }
            }
          }
        }
      } catch (err) {
        if (abort.signal.aborted) return; // intentional teardown
        const name = err instanceof Error ? err.name : '';
        if (name === 'AbortError') return;
        // eslint-disable-next-line no-console
        console.error('[dictation] Transcribe stream error:', {
          name,
          message: err instanceof Error ? err.message : String(err),
          err,
        });
        setErrorKind('network');
        setState('error');
        teardownPipeline();
      }
    })();
  }, [teardownPipeline]);

  // Internal stop variants. The `reason` affects state transitions but not
  // the teardown sequence.
  const stopInternal = useCallback(
    (reason: 'user' | 'timeout' | 'discard') => {
      teardownPipeline();
      clearTick();
      if (reason === 'discard') {
        setFinalText('');
        setPartialText('');
        setWordCount(0);
        setState('idle');
      } else {
        setPartialText('');
        setState('ended');
      }
    },
    [teardownPipeline, clearTick],
  );

  const api: DictationApi = {
    state,
    finalText,
    partialText,
    elapsedSec,
    detectedLang,
    wordCount,
    errorKind,
    requestStart: useCallback(() => {
      if (state === 'idle') setState('permission');
    }, [state]),
    grantAndStart: useCallback(async () => {
      await start();
    }, [start]),
    cancelStart: useCallback(() => {
      if (state === 'permission') setState('idle');
    }, [state]),
    pause: useCallback(() => {
      if (state !== 'live' && state !== 'ending-soon') return;
      // Fully release the mic — per designer: pausing MUST stop audio
      // capture, not just stop sending it. Resume opens a fresh pipeline.
      teardownPipeline();
      clearTick();
      setPartialText('');
      setState('paused');
    }, [state, teardownPipeline, clearTick]),
    resume: useCallback(async () => {
      if (state !== 'paused') return;
      await start();
    }, [state, start]),
    stop: useCallback(() => stopInternal('user'), [stopInternal]),
    discard: useCallback(() => stopInternal('discard'), [stopInternal]),
  };

  // Teardown on unmount — belt + braces so a mid-session navigation can't
  // leave an orphaned MediaStreamTrack or Transcribe connection open.
  useEffect(
    () => () => {
      teardownPipeline();
      clearTick();
    },
    [teardownPipeline, clearTick],
  );

  return api;
}

function countWords(s: string): number {
  const trimmed = s.trim();
  if (!trimmed) return 0;
  return trimmed.split(/\s+/).length;
}
